<template>
  <TransitionRoot :show="open" as="template">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all"
            >
              <DialogTitle
                as="h3"
                class="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
              >
                {{ isEditing ? 'Gebruiker Bewerken' : 'Nieuwe Gebruiker' }}
              </DialogTitle>

              <form @submit.prevent="handleSubmit" class="space-y-4">
                <!-- Name -->
                <div>
                  <label
                    for="name"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Naam</label
                  >
                  <input
                    id="name"
                    v-model="formData.name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Volledige naam"
                  />
                </div>

                <!-- Email -->
                <div>
                  <label
                    for="email"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Email *</label
                  >
                  <input
                    id="email"
                    v-model="formData.email"
                    type="email"
                    required
                    :disabled="isEditing"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600"
                    placeholder="email@frisseblikken.com"
                  />
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Alleen @frisseblikken.com emails zijn toegestaan
                  </p>
                </div>

                <!-- Role -->
                <div class="relative">
                  <label
                    for="role"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Rol</label
                  >
                  <Listbox v-model="formData.role" v-slot="{ open: roleOpen }">
                    <div class="relative">
                      <ListboxButton
                        class="relative w-full cursor-pointer bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white text-sm"
                        :class="
                          roleOpen
                            ? 'rounded-t-lg rounded-b-none border-b-gray-200 dark:border-b-gray-700'
                            : 'rounded-lg'
                        "
                      >
                        <span class="block truncate">
                          {{ selectedRoleLabel }}
                        </span>
                        <span
                          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
                        >
                          <ChevronUpDownIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" />
                        </span>
                      </ListboxButton>
                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                      >
                        <ListboxOptions
                          class="absolute z-50 max-h-96 w-full overflow-auto rounded-b-lg border border-t-0 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-1 shadow-lg focus:outline-none text-sm"
                        >
                          <ListboxOption
                            v-for="role in availableRoles"
                            :key="role.value"
                            :value="role.value"
                            v-slot="{ active, selected }"
                            as="template"
                          >
                            <li
                              :class="[
                                active
                                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                                  : 'text-gray-900 dark:text-gray-200',
                                'relative cursor-pointer select-none py-2 pl-8 pr-4'
                              ]"
                            >
                              <span
                                :class="[
                                  selected ? 'font-medium' : 'font-normal',
                                  'block truncate'
                                ]"
                              >
                                {{ role.label }}
                              </span>
                              <span
                                v-if="selected"
                                class="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600 dark:text-blue-400"
                              >
                                <CheckIcon class="h-4 w-4" />
                              </span>
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                </div>

                <!-- Team Assignment (visible for Performance Lead and Admin) -->
                <div v-if="formData.role === 'performance_lead' || formData.role === 'admin'">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >Teams</label
                  >
                  <div v-if="loadingTeams" class="text-sm text-gray-500 dark:text-gray-400">
                    Laden...
                  </div>
                  <div
                    v-else-if="filteredTeams.length === 0"
                    class="text-sm text-gray-500 dark:text-gray-400"
                  >
                    Geen teams beschikbaar
                  </div>
                  <div
                    v-else
                    class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 space-y-1"
                  >
                    <label
                      v-for="team in filteredTeams"
                      :key="team.simplicate_id"
                      class="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        :value="team.simplicate_id"
                        v-model="formData.team_ids"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                      />
                      <span class="text-sm text-gray-900 dark:text-white">{{ team.name }}</span>
                    </label>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Selecteer de teams waarvoor deze PL verantwoordelijk is
                  </p>
                </div>

                <!-- Active Status -->
                <div class="flex items-center gap-2">
                  <input
                    id="is_active"
                    v-model="formData.is_active"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                  <label
                    for="is_active"
                    class="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >Actief</label
                  >
                </div>

                <!-- Dashboard Access -->
                <div class="flex items-center gap-2">
                  <input
                    id="dashboard_access"
                    v-model="formData.dashboard_access"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  />
                  <label
                    for="dashboard_access"
                    class="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >Dashboard toegang</label
                  >
                </div>

                <!-- Error message -->
                <div
                  v-if="error"
                  class="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm rounded-lg"
                >
                  {{ error }}
                </div>

                <!-- Actions -->
                <div class="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    @click="$emit('close')"
                    class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    :disabled="isSaving"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {{ isSaving ? 'Opslaan...' : 'Opslaan' }}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/24/outline'
import { teamService } from '@/services/employeeService.js'
import { isHiddenTeam } from '@/constants/teams.js'

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  user: {
    type: Object,
    default: null
  },
  availableRoles: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['close', 'save'])

const formData = ref({
  id: null,
  name: '',
  email: '',
  role: 'user',
  is_active: true,
  dashboard_access: false,
  team_ids: []
})

const isSaving = ref(false)
const error = ref('')

const isEditing = ref(false)

// Teams data for multi-select
const allTeams = ref([])
const loadingTeams = ref(false)

const filteredTeams = computed(() =>
  allTeams.value.filter(
    t =>
      !t.name.includes('_OUD_') && t.name !== 'Werkstudenten & Stagiaires' && !isHiddenTeam(t.name)
  )
)

const loadTeams = async () => {
  if (allTeams.value.length > 0) return
  loadingTeams.value = true
  try {
    allTeams.value = await teamService.getAll()
  } catch (e) {
    console.error('Failed to load teams:', e)
  } finally {
    loadingTeams.value = false
  }
}

// Computed property for selected role label
const selectedRoleLabel = computed(() => {
  const role = props.availableRoles.find(r => r.value === formData.value.role)
  return role?.label || 'Selecteer rol...'
})

// Watch for user prop changes to populate form
watch(
  () => props.user,
  newUser => {
    if (newUser) {
      isEditing.value = true
      formData.value = {
        id: newUser.id,
        name: newUser.name || '',
        email: newUser.email,
        role: newUser.role,
        is_active: newUser.is_active,
        dashboard_access: !!newUser.dashboard_access,
        team_ids: newUser.teams ? newUser.teams.map(t => t.simplicate_id) : []
      }
    } else {
      isEditing.value = false
      formData.value = {
        id: null,
        name: '',
        email: '',
        role: 'user',
        is_active: true,
        dashboard_access: false,
        team_ids: []
      }
    }
    error.value = ''
  },
  { immediate: true }
)

// Clear team_ids when role changes to one that doesn't support teams
watch(
  () => formData.value.role,
  newRole => {
    if (newRole !== 'performance_lead' && newRole !== 'admin') {
      formData.value.team_ids = []
    }
  }
)

// Load teams and reset error when modal opens/closes
watch(
  () => props.open,
  isOpen => {
    if (isOpen) {
      loadTeams()
    } else {
      error.value = ''
    }
  }
)

const handleSubmit = async () => {
  // Validate email domain
  if (!formData.value.email.endsWith('@frisseblikken.com')) {
    error.value = 'Alleen @frisseblikken.com emails zijn toegestaan'
    return
  }

  isSaving.value = true
  error.value = ''

  try {
    await emit('save', {
      ...formData.value,
      isEditing: isEditing.value
    })
  } catch (err) {
    error.value = err.message || 'Fout bij opslaan van gebruiker'
  } finally {
    isSaving.value = false
  }
}
</script>
